<template>
  <div
    class="package-card"
    :class="{ 'is-selected': isSelected }"
    @click="handleClick"
  >
    <!-- 标签 -->
    <div v-if="pkg.tagText" class="package-tag">
      <span>{{ pkg.tagText }}</span>
    </div>

    <!-- 主内容 -->
    <div class="package-content">
      <!-- 标题 -->
      <h3 class="package-name">{{ displayInfo.mainText }}</h3>

      <!-- 价格 -->
      <div class="package-price">
        <span class="price-current">¥{{ pkg.price }}</span>
        <span v-if="pkg.originalPrice" class="price-original">¥{{ pkg.originalPrice }}</span>
      </div>

      <!-- 副标题 -->
      <div class="package-subtitle">{{ displayInfo.subText }}</div>
    </div>

    <!-- 选中标记 -->
    <div v-if="isSelected" class="selected-badge">
      <Icon name="material-symbols:check-circle" />
    </div>

    <!-- 底部 Footer 提示 -->
    <div v-if="pkg.footerText" class="package-footer">
      <span class="footer-text" :class="{ 'is-deleted': pkg.footerStyle === 2 }">{{ pkg.footerText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VipPackage, PagePackage } from '~/stores/pay'

interface Props {
  package: VipPackage | PagePackage
  isSelected: boolean
  type: 'vip' | 'page'
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [pkg: VipPackage | PagePackage]
}>()

// 计算显示信息
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const displayInfo = computed(() => {
  const pkg = props.package as any
  
  // 统一逻辑：以会员套餐为准，直接使用后端返回的 name 和 subtitle
  return {
    mainText: pkg.name,
    subText: pkg.subtitle || ''
  }
})

const pkg = computed(() => props.package as any)

const handleClick = () => {
  emit('select', props.package)
}
</script>

<style scoped lang="scss">
.package-card {
  position: relative;
  padding: 24px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 200px;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: #4A90E2;
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.15);
    transform: translateY(-2px);
  }

  &.is-selected {
    border-color: #4A90E2;
    background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
    box-shadow: 0 4px 16px rgba(74, 144, 226, 0.2);
  }
}

.package-tag {
  position: absolute;
  top: -12px;
  right: 20px;

  span {
    display: inline-block;
    padding: 4px 16px;
    background: linear-gradient(135deg, #4A90E2 0%, #50E3C2 100%);
    color: white;
    font-size: 12px;
    font-weight: 600;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);
  }
}

.package-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: center;
}

.package-name {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 12px 0;
}

.package-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;

  .price-current {
    font-size: 32px;
    font-weight: 700;
    color: #4A90E2;
  }

  .price-original {
    font-size: 16px;
    color: #ADB5BD;
    text-decoration: line-through;
  }
}

.package-subtitle {
  font-size: 14px;
  color: #6C757D;
  margin-bottom: 16px;
}

.selected-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 24px;
  color: #4A90E2;
}

.package-footer {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

.package-footer .footer-text {
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #4A90E2 0%, #50E3C2 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(74, 144, 226, 0.25);
}

.package-footer .footer-text.is-deleted {
  text-decoration: line-through;
}

// 响应式
@media (max-width: 768px) {
  .package-card {
    padding: 20px;
    min-height: 180px;
  }

  .package-name {
    font-size: 18px;
  }

  .package-price .price-current {
    font-size: 28px;
  }
}
</style>
